import { FrequencyValue } from "basis";
import { relationshipStatusOptions } from "components/IncreaseCreditLimit/options";
import {
  Application,
  ApplicationStatus,
  FrequencyTypeUpperCase,
} from "components/IncreaseCreditLimit/types";
import { OptionsValues } from "components/types";
import { Account } from "library/accounts/types";
import { ActionMap } from "library/ActionMap";
import { AccountInfo } from "library/hooks/types";
import { yesNoOptions } from "library/sharedOptions";
import { nzEmploymentStatusOptions } from "./constants/nzEmploymentStatusOptions";
import { nzIndustryOptions } from "./constants/nzIndustryOptions";
import { nzOccupationOptions } from "./constants/nzOccupationOptions";
import { nzResidentialStatusOptions } from "./constants/nzResidentialStatusOptions";

export interface State {
  newCreditLimit: string;
  account?: Account;
  accountInfo?: AccountInfo;
  personalInformation: PersonalInformationType;
  residentialAddress: ResidentialAddressType;
  monthlyExpenses: MonthlyExpensesType;
  status?: ApplicationStatus;
  employmentAndIncome: EmploymentIncomeType;
  additionalDebts: AdditionalDebtsType;
}

export type AdditionalDebtsType = {
  nzHaveOtherLoans: YesNoType;
  nzOwingLoan: string;
  nzCurrentLoans: YesNoType;
  nzIntendPayOff: YesNoType;
  nzTotalCreditLimit: string;
  nzTotalAmountOwing: string;
};

export type MonthlyExpensesType = {
  expensesFrequency: ExpensesFrequencyValue;
  nzAllExpensesForHousehold: YesNoType;
  nzHaveOffsetMortgage: YesNoType;
  nzRevolvingMortgage: string;
  nzTotalExpenses: number | null;
};

export type ExpensesFrequencyValue = {
  nzMortgage: FrequencyValue;
  nzSecondaryMortgage: FrequencyValue;
  nzPropertyRates: FrequencyValue;
  nzOtherPropertyExpenses: FrequencyValue;
  nzFoodGroceriesEntertainment: FrequencyValue;
  nzPersonalInsurance: FrequencyValue;
  nzGeneralInsurance: FrequencyValue;
  nzTransportAuto: FrequencyValue;
  nzEducationChildcare: FrequencyValue;
  nzPersonalFitnessMedical: FrequencyValue;
  nzAnyAdditionalExpense: FrequencyValue;
};

export type EmploymentIncomeType = {
  employmentDetails: EmploymentDetailsType;
  employerName: string;
  netIncome: FrequencyValue;
  otherEmploymentDetails: EmploymentDetailsType;
  otherEmployerName: string;
  otherIncome: FrequencyValue;
  otherSourcesOfIncome: YesNoType;
  incomeChangesInFuture: YesNoType;
  nzTotalIncome: number | null;
};

type PersonalInformationType = {
  relationshipStatus: RelationshipStatusType;
  numberOfDependants: string;
};

type ResidentialAddressType = {
  residentialStatus: ResidentialStatusType;
  mortgageOrRent: string;
  timeAtCurrentAddress: {
    years: string;
    months: string;
  };
};

type EmploymentDetailsType = {
  employmentStatus: EmploymentStatusType;
  occupation: OccupationType;
  timeWithCurrentEmployer: {
    years: string;
    months: string;
  };
  industry: IndustryType;
};

export type YesNoType = OptionsValues<typeof yesNoOptions>;

type RelationshipStatusType = OptionsValues<typeof relationshipStatusOptions>;

type ResidentialStatusType = OptionsValues<typeof nzResidentialStatusOptions>;

type OccupationType = OptionsValues<typeof nzOccupationOptions>;

export type EmploymentStatusType = OptionsValues<
  typeof nzEmploymentStatusOptions
>;

export type IndustryType = OptionsValues<typeof nzIndustryOptions>;

export interface LandingPageState {
  account: Account;
  accountInfo: AccountInfo;
}

export interface NewCreditLimitState {
  newCreditLimit: string;
}

export interface CliStatusState {
  status: ApplicationStatus;
}

export interface PersonDetailsState {
  personalInformation: PersonalInformationType;
  residentialAddress: ResidentialAddressType;
}

export interface EmploymentIncomeState {
  employmentAndIncome: EmploymentIncomeType;
}

export interface ExpensesState {
  monthlyExpenses: MonthlyExpensesType;
}

export interface AdditionalDebtsState {
  additionalDebts: AdditionalDebtsType;
}

export enum ActionTypes {
  SUBMIT_NEW_CREDIT_LIMIT = "SUBMIT_NEW_CREDIT_LIMIT",
  LANDING_PAGE = "LANDING_PAGE",
  LOAD_APPLICATION_STATUS = "LOAD_APPLICATION_STATUS",
  SUBMIT_PERSONAL_DETAILS = "SUBMIT_PERSONAL_DETAILS",
  SUBMIT_EMPLOYMENT_INCOME = "SUBMIT_EMPLOYMENT_INCOME",
  SUBMIT_EXPENSES = "SUBMIT_EXPENSES",
  SUBMIT_ADDITIONAL_DEBTS = "SUBMIT_ADDITIONAL_DEBTS",
}

export type Payloads = {
  [ActionTypes.SUBMIT_NEW_CREDIT_LIMIT]: NewCreditLimitState;
  [ActionTypes.LANDING_PAGE]: LandingPageState;
  [ActionTypes.LOAD_APPLICATION_STATUS]: CliStatusState;
  [ActionTypes.SUBMIT_PERSONAL_DETAILS]: PersonDetailsState;
  [ActionTypes.SUBMIT_EMPLOYMENT_INCOME]: EmploymentIncomeState;
  [ActionTypes.SUBMIT_EXPENSES]: ExpensesState;
  [ActionTypes.SUBMIT_ADDITIONAL_DEBTS]: AdditionalDebtsState;
};

export type Actions = ActionMap<ActionTypes, Payloads>;

export type CliFormValues = {
  newCreditLimit: string;
};

export type NzSubmitApplicationRequest = {
  privacy_policy_acknowledgement: boolean;
  application: {
    requested_credit_limit: number;
    personal_information: {
      relationship_status: RelationshipStatusType;
      number_of_dependants: number;
    };
    residential_address: {
      residential_status: ResidentialStatusType;
      mortgage_or_rent: number;
      time_at_current_address_year: number;
      time_at_current_address_month: number;
    };
    employment_details: {
      employment_status: EmploymentStatusType;
      occupation: OccupationType;
      time_with_current_employer_year: number;
      time_with_current_employer_month: number;
    };
    income: {
      net_income: number;
      net_income_frequency: FrequencyTypeUpperCase;
      other_income: number;
      other_income_frequency: FrequencyTypeUpperCase;
    };
    monthly_expenses: {
      living_expenses_shared: YesNoType;
      education_childcare: number;
      transport: number;
      insurances: number;
      utilities: number;
      personal_expenses: number;
      lifestyle: number;
    };
    other_cards_loans: {
      loan_repayments: number;
      total_credit_limit: number;
      total_amounts_owing: number;
    };
  };
};

export type NzParseSubmitApplicationResponseReturnType = {
  application: Application;
  acknowledgementUrl?: string;
  bankConnectUrl?: string;
  cancelUrl?: string;
  latitudePoiUrl?: string;
} | null;
